
import { showWarningNotification } from '../../../notifications/actions';
import { AUDIO_MUTE_BUTTON_ENABLED } from '../../flags/constants';
import { IconMicrophoneEmpty, IconMicrophoneEmptySlash } from '../../icons';
import { MEDIA_TYPE } from '../../media/constants';

import AbstractButton, { type Props } from './AbstractButton';

/**
 * An abstract implementation of a button for toggling audio mute.
 */
export default class AbstractAudioMuteButton<P: Props, S: *>
    extends AbstractButton<P, S> {

    icon = IconMicrophoneEmpty;
    toggledIcon = IconMicrophoneEmptySlash;

    /**
     * Handles clicking / pressing the button, and toggles the audio mute state
     * accordingly.
     *
     * @override
     * @protected
     * @returns {void}
     */
    _handleClick() {
        const now = Date.now();

        this._lastToggleTimes = (this._lastToggleTimes || []).filter(t => now - t < 10000);
        if (this._lastToggleTimes.length >= 5) {
            const { dispatch, t } = this.props;

            if (typeof dispatch === 'function') {
                const titleKey = t ? t('chat.audioRateLimitKey') : 'chat.audioRateLimitKey';
                const descriptionKey = t ? t('chat.audioRateLimitMessage') : 'chat.audioRateLimitMessage';

                dispatch(showWarningNotification({
                    titleKey,
                    description: descriptionKey
                }));
            }

            return;
        }
        this._lastToggleTimes.push(now);

        this._setAudioMuted(!this._isAudioMuted());
    }

    /**
     * Helper function to be implemented by subclasses, which must return a
     * boolean value indicating if audio is muted or not.
     *
     * @protected
     * @returns {boolean}
     */
    _isAudioMuted() {
        // To be implemented by subclass.
    }

    /**
     * Indicates whether this button is in toggled state or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this._isAudioMuted();
    }

    /**
     * Helper function to perform the actual setting of the audio mute / unmute
     * action.
     *
     * @param {boolean} audioMuted - Whether audio should be muted or not.
     * @protected
     * @returns {void}
     */
    _setAudioMuted() {
        // To be implemented by subclass.
    }
}

/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code AbstractAudioMuteButton} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
*     _audioMuted: boolean,
*     _disabled: boolean
* }}
*/
export function mapStateToProps(state: any) {
    const _audioMuted = isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.AUDIO);
    const _disabled = isAudioMuteButtonDisabled(state);
    const enabledFlag = getFeatureFlag(state, AUDIO_MUTE_BUTTON_ENABLED, true);

    return {
        _audioMuted,
        _disabled,
        visible: enabledFlag
    };
}
